
import { format } from 'date-fns';
import { supabase } from "@/integrations/supabase/client";
import { CycleImportRow } from '@/utils/cycle-tracking/import';
import { toast } from 'sonner';

// Helper function to validate UUID format
export const isValidUUID = (id: string): boolean => {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex.test(id);
};

export const importCycleData = async (
  userId: string, 
  data: CycleImportRow[]
): Promise<boolean> => {
  if (!userId) {
    toast.error('You must be logged in to import cycle data.');
    return false;
  }
  
  // Check if user ID is in valid UUID format
  if (!isValidUUID(userId)) {
    toast.error('Invalid user identification. Please try logging out and back in.');
    console.error('Invalid UUID format for user ID:', userId);
    return false;
  }
  
  try {
    console.log('Starting to import data:', data.length, 'records');
    
    // 1. First check if a health profile exists for this user
    const { data: existingProfile, error: profileCheckError } = await supabase
      .from('health_profiles')
      .select('id')
      .eq('user_id', userId)
      .maybeSingle();
    
    if (profileCheckError) {
      console.error('Error checking for existing profile:', profileCheckError);
      throw profileCheckError;
    }
    
    // 1. First update health profile with the most recent period
    if (data.length > 0) {
      // Find the most recent period start date
      const sortedData = [...data].sort((a, b) => 
        b.cycleStartDate.getTime() - a.cycleStartDate.getTime()
      );
      
      const mostRecent = sortedData[0];
      const periodDuration = Math.ceil(
        (mostRecent.cycleEndDate.getTime() - mostRecent.cycleStartDate.getTime()) / (1000 * 60 * 60 * 24)
      );
      
      console.log('Most recent period:', format(mostRecent.cycleStartDate, 'yyyy-MM-dd'), 'to', 
                  format(mostRecent.cycleEndDate, 'yyyy-MM-dd'), 'duration:', periodDuration);
      
      if (!existingProfile) {
        // Create a new health profile if none exists
        const { error: createProfileError } = await supabase
          .from('health_profiles')
          .insert({
            user_id: userId,
            last_period_start: format(mostRecent.cycleStartDate, 'yyyy-MM-dd'),
            last_period_duration: periodDuration.toString(),
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          });
        
        if (createProfileError) {
          console.error('Error creating health profile:', createProfileError);
          throw createProfileError;
        }
      } else {
        // Update existing health profile
        const { error: updateProfileError } = await supabase
          .from('health_profiles')
          .update({
            last_period_start: format(mostRecent.cycleStartDate, 'yyyy-MM-dd'),
            last_period_duration: periodDuration.toString(),
            updated_at: new Date().toISOString()
          })
          .eq('user_id', userId);
        
        if (updateProfileError) {
          console.error('Error updating health profile:', updateProfileError);
          throw updateProfileError;
        }
      }
    }
    
    // 2. Import all the periods into the period_history table
    const periodHistoryToInsert = data.map(entry => {
      const periodLength = Math.ceil(
        (entry.cycleEndDate.getTime() - entry.cycleStartDate.getTime()) / (1000 * 60 * 60 * 24)
      );
      
      // Calculate cycle length if possible (days between this period and the next one)
      let cycleLength = null;
      if (entry.nextPeriodStart) {
        cycleLength = Math.ceil(
          (entry.nextPeriodStart.getTime() - entry.cycleStartDate.getTime()) / (1000 * 60 * 60 * 24)
        );
      }
      
      return {
        user_id: userId,
        period_start_date: format(entry.cycleStartDate, 'yyyy-MM-dd'),
        period_end_date: format(entry.cycleEndDate, 'yyyy-MM-dd'),
        period_length: periodLength,
        cycle_length: cycleLength,
        data_source: 'import',
        notes: entry.notes || `Imported period data from ${format(entry.cycleStartDate, 'yyyy-MM-dd')} to ${format(entry.cycleEndDate, 'yyyy-MM-dd')}`
      };
    });
    
    console.log('Inserting period history records:', periodHistoryToInsert.length);
    
    // Insert period history if any
    if (periodHistoryToInsert.length > 0) {
      const { error } = await supabase
        .from('period_history')
        .insert(periodHistoryToInsert);
        
      if (error) {
        console.error('Error inserting period history:', error);
        throw error;
      }
    }
    
    // 3. Then import all the symptom logs
    const symptomLogsToInsert = data.flatMap(entry => {
      const logs = [];
      
      // Add period symptoms log
      if (entry.symptoms && entry.symptoms.length > 0) {
        logs.push({
          user_id: userId,
          symptoms: entry.symptoms,
          intensity_level: entry.mood === 'High' ? 5 : entry.mood === 'Medium' ? 3 : 1,
          logged_at: entry.cycleStartDate.toISOString(),
          notes: entry.notes || `Imported period data: ${format(entry.cycleStartDate, 'yyyy-MM-dd')} to ${format(entry.cycleEndDate, 'yyyy-MM-dd')}`
        });
      }
      
      // Add medication log if present
      if (entry.medications && entry.medications.length > 0 && entry.medications[0] !== 'None') {
        logs.push({
          user_id: userId,
          symptoms: ['Taking Medication'],
          custom_symptoms: entry.medications,
          logged_at: entry.cycleStartDate.toISOString(),
          notes: `Medication: ${entry.medications.join(', ')}`
        });
      }
      
      // Add contraceptive log if present
      if (entry.contraceptiveUse && entry.contraceptiveUse !== 'None') {
        logs.push({
          user_id: userId,
          symptoms: ['Using Contraception'],
          custom_symptoms: [entry.contraceptiveUse],
          logged_at: entry.cycleStartDate.toISOString(),
          notes: `Contraceptive: ${entry.contraceptiveUse}`
        });
      }
      
      return logs;
    });
    
    console.log('Inserting symptom logs:', symptomLogsToInsert.length);
    
    // Insert symptom logs if any
    if (symptomLogsToInsert.length > 0) {
      const { error } = await supabase
        .from('symptom_logs')
        .insert(symptomLogsToInsert);
        
      if (error) {
        console.error('Error inserting symptom logs:', error);
        throw error;
      }
    }
    
    return true;
  } catch (error) {
    console.error('Error importing cycle data:', error);
    return false;
  }
};
